import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useAuthHeaders } from "../auth/apiClient";

export type AgentLearningStatus = "new" | "watching" | "accepted" | "rejected" | "experiment";

export type AgentLearningInsight = {
  id: number;
  tenant_id: number;
  insight_key: string;
  category: "timing" | "copy" | "conversion" | "dashboard" | "compliance" | "voice" | "data_quality";
  title: string;
  summary: string;
  recommendation: string;
  evidence: Record<string, unknown>;
  confidence: "low" | "medium" | "high";
  risk: "low" | "medium" | "high";
  status: AgentLearningStatus;
  source: string;
  created_at: string;
  updated_at: string;
};

export type TemplatePerformanceItem = {
  template_family: string;
  channel: string;
  sends: number;
  replies: number;
  scheduled_after_send: number;
  opt_outs: number;
  reply_rate: number;
  conversion_rate: number;
  opt_out_rate: number;
  confidence: "very_low" | "low" | "medium";
  verdict: "needs_data" | "watch" | "candidate" | "risk_review";
  sample_template_ids: string[];
};

export type HumanDecisionReviewStatus = "pending_review" | "validated" | "corrected" | "rejected";

export type HumanDecisionRejectReason = "checker_noise" | "operator_bookkeeping" | "non_decision";

export type ReviewCaseQueueStatus = "needs_marc" | "closed_by_status" | "decided";

export type ReviewCaseDecision = {
  id: number;
  source?: "review_decisions" | "human_decision_training_signals";
  ts: string | null;
  human_verdict: string;
  reason_codes: string[];
  correction: string | null;
  note: string | null;
  golden_flag: boolean;
  decided_by: string | null;
};

export type ReviewCase = {
  case_type: "label_proposal" | "checker_stop" | "unknown";
  case_ref: string;
  quote_id: string | null;
  channel: string | null;
  customer_name: string | null;
  customer_message_excerpt: string;
  proposed_label: string | null;
  proposed_semantic: string | null;
  proposed_action: string | null;
  checker_verdict: string | null;
  reason_codes: string[];
  evidence_snippet: string | null;
  draft_excerpt: string | null;
  proposal_date: string | null;
  current_vonigo_status: string | null;
  current_vonigo_raw_label: string | null;
  queue_status: ReviewCaseQueueStatus;
  decision: ReviewCaseDecision | null;
};

export type ReviewCasesResponse = {
  items: ReviewCase[];
  summary: {
    needs_marc: number;
    closed_by_status: number;
    decided: number;
  };
  guardrails: {
    mode: "teach_queue";
    customer_impact: "none";
    can_modify_templates: false;
    can_send_messages: false;
    can_write_vonigo: false;
    can_change_flags: false;
    requires_owner_approval: true;
  };
};

export type HumanDecisionSignal = {
  id: number;
  tenant_id: number;
  quote_id: string;
  channel: string;
  decision_type: string;
  agent_proposal: string;
  agent_proposal_semantic: string | null;
  human_decision: string;
  human_decision_semantic: string | null;
  disposition_type: string | null;
  source_table: string;
  source_pk: string | null;
  source_ref: string;
  idempotency_key: string;
  snapshot: Record<string, unknown>;
  checker_verdict_at_correction: Record<string, unknown> | null;
  captured_by: string;
  captured_at: string;
  review_status: HumanDecisionReviewStatus;
  reviewed_by: string | null;
  reviewed_at: string | null;
  review_note: string | null;
  supersedes_signal_id: number | null;
  created_at: string;
};

export type HumanDecisionSignalsResponse = {
  items: HumanDecisionSignal[];
  summary: {
    total: number;
    by_type: Record<string, number>;
    by_status: Record<string, number>;
  };
  guardrails: {
    mode: "learning_data_only" | "learning_review_only";
    customer_impact: "none" | "none_until_thinking_flag_enabled";
    can_send_messages: false;
    can_write_vonigo: false;
    can_write_thinking_about_it_on_validate?: "flag_gated";
    thinking_about_it_write_flag?: "VONIGO_LABEL_LIVE_THINKING_ABOUT_IT";
    can_write_opt_outs: false;
    can_change_flags: false;
    promotion_requires_human_review?: true;
    promotion_to_golden?: "manual_only";
  };
};

export type ReviewHumanDecisionSignalResponse = {
  item: HumanDecisionSignal;
  previous_item?: HumanDecisionSignal;
  correction_item?: HumanDecisionSignal;
  inserted: boolean;
  idempotent: boolean;
  restored_review_status?: boolean;
  restore_error?: string;
  thinking_label_write?: {
    ok: boolean;
    status: string;
    signal_id?: number;
    tenant_id?: number;
    quote_id?: string | null;
    semantic?: "thinking_about_it";
    wrote: boolean;
    audit_id?: number | null;
    audit_recorded?: boolean;
    previous_option_id?: number | null;
    new_option_id?: number | null;
    error?: string | null;
    exit_code?: number;
    duration_ms?: number;
    truncated?: boolean;
  };
  guardrails: HumanDecisionSignalsResponse["guardrails"];
};

export type ReviewThinkingLabelProposalResponse = ReviewHumanDecisionSignalResponse & {
  review_case?: ReviewCase;
  signal_inserted?: boolean;
  signal_idempotent?: boolean;
};

type TemplatePerformanceResponse = {
  days_back: number;
  total_messages: number;
  items: TemplatePerformanceItem[];
  caveats: string[];
  guardrails: {
    mode: "read_only_scoreboard";
    customer_impact: "none";
    can_modify_templates: false;
    can_send_messages: false;
    requires_owner_approval: true;
  };
};

type AgentLearningResponse = {
  items: AgentLearningInsight[];
  generated?: number;
  guardrails: {
    mode: "observation_only";
    customer_impact: "none";
    can_modify_templates: false;
    can_send_messages: false;
    requires_owner_approval: true;
  };
};

async function parseJson<T>(res: Response): Promise<T> {
  if (!res.ok) throw new Error(`Agent learning request failed: ${res.status}`);
  return res.json() as Promise<T>;
}

export function humanDecisionSignalsUrl(reviewStatus: HumanDecisionReviewStatus | "all" = "pending_review") {
  const params = new URLSearchParams({
    review_status: reviewStatus,
    limit: "200",
  });
  return `/api/cockpit/agent-learning/human-decision-signals?${params.toString()}`;
}

export function humanDecisionSignalReviewUrl(id: number, action: "validate" | "correct" | "reject") {
  return `/api/cockpit/agent-learning/human-decision-signals/${id}/${action}`;
}

export function reviewCasesUrl() {
  const params = new URLSearchParams({
    limit: "200",
    include_closed: "0",
  });
  return `/api/cockpit/agent-learning/review-cases?${params.toString()}`;
}

export function thinkingLabelProposalReviewUrl(id: string, action: "validate" | "reject") {
  return `/api/cockpit/agent-learning/review-cases/label-proposals/${encodeURIComponent(id)}/${action}`;
}

export function buildReviewHumanDecisionSignalBody({
  action,
  correction,
  reason,
  note,
}: {
  action: "validate" | "correct" | "reject";
  correction?: { human_decision: string };
  reason?: HumanDecisionRejectReason;
  note?: string;
}) {
  if (action === "correct") return { correction, review_note: note || undefined };
  if (action === "reject") return { reason, review_note: note || undefined };
  return { review_note: note || undefined };
}

export function useAgentLearningInsights() {
  const headers = useAuthHeaders();
  return useQuery({
    queryKey: ["agent-learning", "insights"],
    queryFn: async () => {
      const res = await fetch("/api/cockpit/agent-learning", { headers });
      return parseJson<AgentLearningResponse>(res);
    },
    staleTime: 60_000,
  });
}

export function useRunAgentLearning() {
  const headers = useAuthHeaders(true);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await fetch("/api/cockpit/agent-learning/run", {
        method: "POST",
        headers,
        body: JSON.stringify({ days_back: 30 }),
      });
      return parseJson<AgentLearningResponse>(res);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["agent-learning", "insights"] }),
  });
}

export function useUpdateAgentLearningStatus() {
  const headers = useAuthHeaders(true);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, status }: { id: number; status: AgentLearningStatus }) => {
      const res = await fetch(`/api/cockpit/agent-learning/${id}/status`, {
        method: "POST",
        headers,
        body: JSON.stringify({ status }),
      });
      return parseJson<{ item: AgentLearningInsight }>(res);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["agent-learning", "insights"] }),
  });
}

export function useTemplatePerformance() {
  const headers = useAuthHeaders();
  return useQuery({
    queryKey: ["agent-learning", "template-performance"],
    queryFn: async () => {
      const res = await fetch("/api/cockpit/agent-learning/template-performance?days_back=180&limit=12", { headers });
      return parseJson<TemplatePerformanceResponse>(res);
    },
    staleTime: 5 * 60_000,
  });
}

export function useHumanDecisionSignals(reviewStatus: HumanDecisionReviewStatus | "all" = "pending_review") {
  const headers = useAuthHeaders();
  return useQuery({
    queryKey: ["agent-learning", "human-decision-signals", reviewStatus],
    queryFn: async () => {
      const res = await fetch(humanDecisionSignalsUrl(reviewStatus), { headers });
      return parseJson<HumanDecisionSignalsResponse>(res);
    },
    staleTime: 30_000,
  });
}

export function useReviewCases() {
  const headers = useAuthHeaders();
  return useQuery({
    queryKey: ["agent-learning", "review-cases"],
    queryFn: async () => {
      const res = await fetch(reviewCasesUrl(), { headers });
      return parseJson<ReviewCasesResponse>(res);
    },
    staleTime: 30_000,
  });
}

export function useReviewThinkingLabelProposal() {
  const headers = useAuthHeaders(true);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, action, note }: { id: string; action: "validate" | "reject"; note?: string }) => {
      const res = await fetch(thinkingLabelProposalReviewUrl(id, action), {
        method: "POST",
        headers,
        body: JSON.stringify({
          review_note: note || undefined,
          reason: action === "reject" ? "operator_bookkeeping" : undefined,
        }),
      });
      return parseJson<ReviewThinkingLabelProposalResponse>(res);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["agent-learning", "review-cases"] });
      queryClient.invalidateQueries({ queryKey: ["agent-learning", "human-decision-signals"] });
    },
  });
}

export function useReviewHumanDecisionSignal() {
  const headers = useAuthHeaders(true);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      action,
      correction,
      reason,
      note,
    }: {
      id: number;
      action: "validate" | "correct" | "reject";
      correction?: { human_decision: string };
      reason?: HumanDecisionRejectReason;
      note?: string;
    }) => {
      const res = await fetch(humanDecisionSignalReviewUrl(id, action), {
        method: "POST",
        headers,
        body: JSON.stringify(buildReviewHumanDecisionSignalBody({ action, correction, reason, note })),
      });
      return parseJson<ReviewHumanDecisionSignalResponse>(res);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["agent-learning", "human-decision-signals"] });
    },
  });
}
